import React, { useState, useEffect } from "react";
import Box from "@material-ui/core/Box";
import Popper from "@material-ui/core/Popper";
import Text from "shared/components/data-display/mui/Text";
import PropTypes from "prop-types";
import { useTheme } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import MenuList from "@material-ui/core/MenuList";
import Button from "shared/components/inputs/mui/Button";
import DownCaret from "shared/components/utils/icons/DownCaret";
import IconButton from "@material-ui/core/IconButton";
import { truncateText } from "shared/helper-functions/string/truncateText";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import { useStylesForMenu, useStylesForText } from "shared/styles/makeStyles";
import CustomMenuItem from "./CustomMenuItem";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import Loader from "shared/components/feedback/loading/Loader";
import {
    CustomListMenu,
    InfoLabel,
    SelectedLabel,
    Label,
    CustomMenuButton,
    MenuTab,
    SearchInput,
    MenuButton,
    LoaderWrapper,
    StyledBox,
    ClearIcon,
} from "./CustomMenu.styles";

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    const theme = useTheme();

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <StyledBox
                    p={3}
                    fontSize={theme.typography.smallLight.fontSize}
                    fontFamily={theme.typography.smallLight.fontFamily}
                >
                    {children}
                </StyledBox>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

export default function PopperPopupState(props) {
    const {
        menuLabel,
        tabs,
        itemsLabel,
        action,
        itemAction,
        moreMenuOptions,
        getItemsFirstTab,
        getItemsSecondTab,
    } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const classes = useStylesForMenu();
    const [value, setValue] = useState(0);
    const [filter, setFilter] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemsFirstTab, setItemsFirstTab] = useState(null);
    const [itemsSecondTab, setItemsSecondTab] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const classesText = useStylesForText();

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoupClose = () => {
        if (anchorEl !== null) {
            setAnchorEl(null);
        }
    };

    const handleChangeTab = (_event, newValue) => {
        setValue(newValue);
    };

    const handleChangeFilter = (event) => {
        setFilter(event.target.value);
    };

    const handleAction = (event) => {
        action(event);
        handlePopoupClose();
    };

    const clearSelection = (e) => {
        e.stopPropagation();
        setSelectedItem(null);
        itemAction(e, null);
        if (props.onSelectItem) props.onSelectItem(null);
    };

    const loadData = async () => {
        setIsLoadingData(true);
        if (!open) {
            const r1 = await getItemsFirstTab();
            setItemsFirstTab(r1);
            setIsLoadingData(false);
            const r2 = await getItemsSecondTab();
            setItemsSecondTab(r2);
            setIsLoadingData(false);
        }
    };

    const firstTabItems =
        itemsFirstTab?.filter(
                (item) =>
                    filter === "" ||
                    item.Name.toLowerCase().includes(filter.toLowerCase())
            )
            .map((item) => (
                <CustomMenuItem
                    key={item.Id}
                    value={item.Id}
                    name={item.Name}
                    owned={item.Own ?? true}
                    moreMenuOptions={moreMenuOptions}
                    selectedItem={selectedItem}
                    onClickItem={function (event) {
                        setSelectedItem(item);
                        itemAction(event, item);
                        if (props.onSelectItem) props.onSelectItem(item);
                        handlePopoupClose();
                    }}
                    closeMenu={handlePopoupClose}
                />
            ));

    const secondTabItems =
        itemsSecondTab?.filter(
                (item) =>
                    filter === "" ||
                    item.Name.toLowerCase().includes(filter.toLowerCase())
            )
            .map((item) => (
                <CustomMenuItem
                    key={item.Id}
                    value={item.Id}
                    name={item.Name}
                    owned={item.Own ?? true}
                    moreMenuOptions={moreMenuOptions}
                    selectedItem={selectedItem}
                    onClickItem={function (event) {
                        setSelectedItem(item);
                        itemAction(event, item);
                        if (props.onSelectItem) props.onSelectItem(item);
                        handlePopoupClose();
                    }}
                    closeMenu={handlePopoupClose}
                />
            ));

    const MenuTabItems = tabs.map((itemLabel, index) => (
        <MenuTab
            key={itemLabel}
            label={itemLabel}
            {...a11yProps(index)}
        />
    ));

    useEffect(() => {
        setSelectedItem(props.value);
    }, [props.value]);

    return (
        <ClickAwayListener onClickAway={handlePopoupClose}>
            <CustomListMenu>
                <CustomMenuButton
                    className={open ? classes.openMenu : ""}
                    onClick={(e) => {
                        loadData();
                        handleClick(e);
                    }}
                >
                    <Label>
                        {menuLabel}
                        {selectedItem != null && (
                            <SelectedLabel>
                                <Tooltip
                                    placement="bottom"
                                    title={selectedItem.Name}
                                >
                                    : {truncateText(selectedItem.Name, 120)}
                                </Tooltip>
                                <IconButton
                                    aria-label="clear"
                                    onClick={clearSelection}
                                    size="small"
                                    className={classes.clearButton}
                                >
                                    <ClearIcon>X</ClearIcon>
                                </IconButton>
                            </SelectedLabel>
                        )}
                    </Label>
                    <DownCaret size="16"></DownCaret>
                </CustomMenuButton>
                <Popper
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handlePopoupClose}
                    placement="bottom-start"
                    className={classes.customMenu}
                    disablePortal={true}
                >
                    <Box className={classes.root} variant="smallLight">
                        <Tabs
                            value={value}
                            onChange={handleChangeTab}
                            aria-label="custom groups tabs"
                            indicatorColor="primary"
                            textColor="primary"
                            variant="fullWidth"
                        >
                            {MenuTabItems}
                        </Tabs>
                        <SearchInput
                            placeholder="Search"
                            value={filter}
                            onChange={handleChangeFilter}
                            className={classes.searchInput}
                        />
                        {isLoadingData ? (
                            <LoaderWrapper>
                                <Loader height="1rem" width="1rem" />
                            </LoaderWrapper>
                        ) : (
                            <>
                                <InfoLabel>
                                    <Text
                                        variant="xxsmallLight"
                                        className={classesText.gray2}
                                    >
                                        {itemsLabel}
                                        {value === 0
                                            ? firstTabItems &&
                                              " (" + firstTabItems.length + ")"
                                            : secondTabItems &&
                                              " (" +
                                                  secondTabItems.length +
                                                  ")"}
                                    </Text>
                                </InfoLabel>
                                <TabPanel value={value} index={0}>
                                    <MenuList>{firstTabItems}</MenuList>
                                </TabPanel>
                                <TabPanel value={value} index={1}>
                                    <MenuList>{secondTabItems}</MenuList>
                                </TabPanel>
                            </>
                        )}
                        <MenuButton>
                            <Button onClick={handleAction}>
                                {props.children}
                            </Button>
                        </MenuButton>
                    </Box>
                </Popper>
            </CustomListMenu>
        </ClickAwayListener>
    );
}

PopperPopupState.propTypes = {
    menuLabel: PropTypes.any.isRequired,
    tabs: PropTypes.any.isRequired,
    moreMenuOptions: PropTypes.node,
    getItemsFirstTab: PropTypes.any.isRequired,
    getItemsSecondTab: PropTypes.any.isRequired,
    itemsLabel: PropTypes.string,
    action: PropTypes.func,
    itemAction: PropTypes.func,
    value: PropTypes.any,
    children: PropTypes.node,
    onSelectItem: PropTypes.func,
};
